import { API } from 'aws-amplify';
import Constant from '@/common/constant/Constant';
import Utils from '@/common/utils/utils';
import { publicGetKnowledgeListAPI } from '@/service/publicService';
import { getDefaultDataForChatBot } from '../../publicKnowledge/handler/PublicKnowledgeListHandler';
import axios from 'axios';

/**
 * Resolve API response — handles S3 presigned URL transparently.
 * If response contains s3Url, fetches from S3 and returns parsed data.
 */
const resolvePayload = async (response) => {
    if (!response?.payload) return null;
    const parsed = Utils.convertJSONDataToObject(response.payload);
    if (!parsed) return null;

    if (parsed.s3Url) {
        const s3Response = await axios.get(parsed.s3Url);
        const s3Data = s3Response.data;
        if (parsed.totalTicketInList !== undefined) s3Data.totalTicketInList = parsed.totalTicketInList;
        if (parsed.totalTicketMatching !== undefined) s3Data.totalTicketMatching = parsed.totalTicketMatching;
        return s3Data;
    }

    return parsed;
};

/**
 * Fetch drilldown stats for initial load (Action 1).
 * Called when user selects a category.
 */
export const fetchDrilldownStats = async ({ projectId, subDomain, structure, corporationId, viewId }) => {
    const response = await publicGetKnowledgeListAPI(API, {
        getApiType: Constant.GET_API_PUBLIC_TYPE.GET_PUBLIC_KNOWLEDGE_DRILLDOWN_DATA_FOR_CHATBOT_V2,
        subDomain,
        projectId,
        structure,
        corporationId,
        viewId
    });

    const parsedPayload = await resolvePayload(response);
    if (!parsedPayload) return { tagFieldList: [], totalTicketInList: 0 };

    const drillDownData = getDefaultDataForChatBot(parsedPayload, structure);
    drillDownData.totalTicketInList = parsedPayload.totalTicketInList;

    return drillDownData;
};

/**
 * Fetch items of a specific field (Action 2).
 * Called when user selects a tag field.
 */
export const fetchFieldItems = async ({
    projectId, subDomain, structure, corporationId,
    targetFieldId, tagFieldQuery, listTagFieldSelected, listTagItemSelected
}) => {
    const response = await publicGetKnowledgeListAPI(API, {
        getApiType: Constant.GET_API_PUBLIC_TYPE.GET_CHATBOT_FILTERED_DRILLDOWN,
        subDomain,
        corporationId,
        projectId,
        structure,
        tagFieldQuery,
        targetFieldId,
        listTagFieldSelected,
        listTagItemSelected,
        listTagFieldSkip: []
    });

    const parsedPayload = await resolvePayload(response);
    if (!parsedPayload) return { items: [], totalTicketMatching: 0 };

    const { tagFieldList, totalTicketMatching } = parsedPayload;
    const targetField = tagFieldList?.find(f => f.id === targetFieldId);

    return {
        items: targetField?.child || [],
        totalTicketMatching: totalTicketMatching || 0
    };
};

/**
 * Fetch remaining fields after user selects an item (Action 3).
 * Called when user selects a tag item.
 */
export const fetchRemainingFields = async ({
    projectId, subDomain, structure, corporationId,
    tagFieldQuery, listTagFieldSelected, listTagItemSelected, listTagFieldSkip
}) => {
    const response = await publicGetKnowledgeListAPI(API, {
        getApiType: Constant.GET_API_PUBLIC_TYPE.GET_CHATBOT_FILTERED_DRILLDOWN,
        subDomain,
        corporationId,
        projectId,
        structure,
        tagFieldQuery,
        listTagFieldSelected,
        listTagItemSelected,
        listTagFieldSkip
    });

    const parsedPayload = await resolvePayload(response);
    if (!parsedPayload) return { remainingFields: [], totalTicketMatching: 0 };

    // Enrich label for fields (backend returns data.name but UI expects .label at field level)
    const tagFieldList = (parsedPayload.tagFieldList || []).map(field => ({
        ...field,
        label: field.label || Utils.getTextByLanguage(Constant.LANGUAGE_TYPE.JP, field.data?.name) || field.data?.name
    }));

    return {
        remainingFields: tagFieldList,
        totalTicketMatching: parsedPayload.totalTicketMatching || 0
    };
};

/**
 * Decide what chatbot should show next.
 * Combines ticket threshold check + field priority logic.
 */
export const decideNextStep = ({ totalTicketMatching, remainingFields, maxHitTicket, enableListTicket }) => {
    if (totalTicketMatching <= maxHitTicket && enableListTicket) {
        return { type: Constant.CHATBOT_NEXT_STEP.SHOW_TICKET_LIST };
    }

    if (!remainingFields || remainingFields.length === 0) {
        return { type: Constant.CHATBOT_NEXT_STEP.SHOW_TICKET_LIST };
    }

    if (remainingFields[0]?.priority || remainingFields.length === 1) {
        return { type: Constant.CHATBOT_NEXT_STEP.SHOW_FIELD_PRIORITY, field: remainingFields[0] };
    }

    return { type: Constant.CHATBOT_NEXT_STEP.SHOW_FIELD_LIST, fields: remainingFields };
};

/**
 * Enrich remaining fields with priority from category settings and sort.
 * Backend does not return priority — it's a client-side category config.
 * After enriching, sorts fields so priority fields come first, then by order ascending.
 * @param {Array} remainingFields - Fields from fetchRemainingFields
 * @param {Array} listTagFieldOfSelectedCategory - Category tag fields with priority/order from initial load
 * @returns {Array} - Fields enriched with priority property and sorted
 */
export const enrichFieldsWithPriority = (remainingFields, listTagFieldOfSelectedCategory) => {
    if (!remainingFields || !listTagFieldOfSelectedCategory) return remainingFields || [];
    const categoryFieldIds = listTagFieldOfSelectedCategory.map(f => f.id);
    const filtered = remainingFields.filter(field => categoryFieldIds.includes(field.id));
    const enriched = filtered.map(field => {
        const categorySetting = listTagFieldOfSelectedCategory.find(f => f.id === field.id);
        if (categorySetting) {
            return { ...field, priority: categorySetting.priority, order: categorySetting.order };
        }
        return field;
    });
    return enriched.sort((a, b) => {
        if (a.priority && !b.priority) return -1;
        if (!a.priority && b.priority) return 1;
        return (a.order || 0) - (b.order || 0);
    });
};
